import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import { useTerminologia } from '../hooks/useTerminologia';

const ESTADO_OPTIONS = [
    { label: 'Todos', value: null },
    { label: 'Pendientes', value: false },
    { label: 'Entregados', value: true },
];

const PaquetesAdmin = () => {
    const toast    = useRef(null);
    const HOST_API = process.env.REACT_APP_URL;
    const { barrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();
    const t = useTerminologia();

    const [paquetes,      setPaquetes]      = useState([]);
    const [ufs,           setUfs]           = useState([]);
    const [loading,       setLoading]       = useState(false);
    const [fechaDesde,    setFechaDesde]    = useState(null);
    const [fechaHasta,    setFechaHasta]    = useState(null);
    const [ufFiltro,      setUfFiltro]      = useState(null);
    const [estadoFiltro,  setEstadoFiltro]  = useState(null);

    // ─── Fetch ────────────────────────────────────────────────────────────────────

    const fetchPaquetes = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({ administracionId, barrioId: barrio.id });
            if (estadoFiltro !== null && estadoFiltro !== undefined) params.append('soloEntregados', estadoFiltro);
            const { data } = await axios.get(`${HOST_API}api/paquetes/GetPaquetes?${params}`);

            let result = data;
            if (fechaDesde) result = result.filter(p => new Date(p.fechaRecepcion) >= fechaDesde);
            if (fechaHasta) {
                const hasta = new Date(fechaHasta); hasta.setHours(23, 59, 59);
                result = result.filter(p => new Date(p.fechaRecepcion) <= hasta);
            }
            if (ufFiltro) result = result.filter(p => p.unidadFuncionalId === ufFiltro);

            setPaquetes(result);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los paquetes', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [HOST_API, barrio?.id, estadoFiltro, fechaDesde, fechaHasta, ufFiltro, administracionId]);

    useEffect(() => { fetchPaquetes(); }, [fetchPaquetes]);

    useEffect(() => {
        if (!barrio?.id) return;
        axios.get(`${HOST_API}api/unidadesFuncionales/getUnidadesFuncionales?unidadFuncionalId=0&administracionId=${administracionId}&barrioId=${barrio.id}`)
            .then(r => setUfs(r.data))
            .catch(() => {});
    }, [HOST_API, barrio?.id, administracionId]);

    // ─── Render helpers ───────────────────────────────────────────────────────────

    const fmtFecha = (val) => val ? new Date(val).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

    const estadoBadge = (row) => {
        const s = row.entregado
            ? { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5', label: 'Entregado' }
            : { bg: '#fef3f2', color: '#f04438', border: '#fecdca', label: 'Pendiente' };
        return (
            <span style={{
                background: s.bg, color: s.color, border: `1px solid ${s.border}`,
                fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
            }}>
                {s.label}
            </span>
        );
    };

    // ─── Render ───────────────────────────────────────────────────────────────────

    return (
        <>
            <h4>Paquetes — Administración</h4>
            <br />
            <Toast ref={toast} />
            {loading && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ProgressSpinner style={{ width: 52, height: 52 }} strokeWidth="4" />
                </div>
            )}

            {/* ─── Filtros ─────────────────────────────────────────────────────── */}
            <div className="flex flex-wrap align-items-end gap-3 mb-4">
                <div>
                    <label style={lbl}>Desde</label>
                    <Calendar value={fechaDesde} onChange={(e) => setFechaDesde(e.value)} dateFormat="dd/mm/yy" showIcon showButtonBar className="p-inputtext-sm" style={{ width: 165 }} placeholder="Desde..." />
                </div>
                <div>
                    <label style={lbl}>Hasta</label>
                    <Calendar value={fechaHasta} onChange={(e) => setFechaHasta(e.value)} dateFormat="dd/mm/yy" showIcon showButtonBar className="p-inputtext-sm" style={{ width: 165 }} placeholder="Hasta..." />
                </div>
                <div>
                    <label style={lbl}>{t.UF}</label>
                    <Dropdown
                        value={ufFiltro}
                        options={[{ unidadFuncionalId: null, unidadFuncional: 'Todas' }, ...ufs]}
                        optionLabel="unidadFuncional"
                        optionValue="unidadFuncionalId"
                        onChange={(e) => setUfFiltro(e.value)}
                        placeholder="Todas las UF"
                        className="p-inputtext-sm"
                        style={{ width: 150 }}
                        filter
                    />
                </div>
                <div>
                    <label style={lbl}>Estado</label>
                    <Dropdown
                        value={estadoFiltro}
                        options={ESTADO_OPTIONS}
                        onChange={(e) => setEstadoFiltro(e.value)}
                        className="p-inputtext-sm"
                        style={{ width: 140 }}
                    />
                </div>
                <Button label="Buscar" icon="pi pi-search" onClick={fetchPaquetes} className="p-button-sm" />
            </div>

            {/* ─── Tabla ───────────────────────────────────────────────────────── */}
            <div className="grid">
                <div className="col-12">
                    <DataTable
                        value={paquetes}
                        paginator
                        rows={15}
                        rowsPerPageOptions={[10, 15, 25, 50]}
                        responsiveLayout="scroll"
                        emptyMessage="No hay paquetes para los filtros seleccionados"
                        style={{ fontSize: '0.88rem' }}
                        sortField="fechaRecepcion"
                        sortOrder={-1}
                    >
                        <Column field="nroPedido"      header="Nro. Pedido"  sortable style={{ fontWeight: 500 }} />
                        <Column field="origenEnvio"    header="Origen"       sortable style={{ width: '10rem' }} />
                        <Column field="codigoUF"       header={t.UF}           sortable style={{ width: '6rem' }} />
                        <Column field="destinatario"   header="Destinatario" sortable />
                        <Column field="encargadoRecibe" header="Recibió"     sortable style={{ width: '12rem' }} />
                        <Column field="fechaRecepcion" header="Fecha Recep." sortable body={(row) => fmtFecha(row.fechaRecepcion)} style={{ width: '11rem' }} />
                        <Column field="encargadoEntrega" header="Entregó"    sortable style={{ width: '12rem' }} body={(row) => row.encargadoEntrega || '—'} />
                        <Column field="nombreReceptor" header="Receptor"     sortable style={{ width: '11rem' }} body={(row) => row.nombreReceptor || '—'} />
                        <Column field="fechaEntrega"   header="Fecha Entrega" sortable body={(row) => fmtFecha(row.fechaEntrega)} style={{ width: '11rem' }} />
                        <Column field="observaciones"  header="Observaciones" />
                        <Column header="Estado" style={{ width: '8rem' }} body={estadoBadge} />
                    </DataTable>
                </div>
            </div>
        </>
    );
};

const lbl = { display: 'block', marginBottom: '0.3rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };

export default PaquetesAdmin;
